import RiCloseLine from '@ben/icons/close-line';
import type {
  ModalBodyProps,
  ModalHeaderProps,
  ModalProps,
} from '@mantine/core';
import { Modal } from '@mantine/core';
import clsx from 'clsx';
import { forwardRef, memo, type ComponentPropsWithoutRef } from 'react';
import { RIcon } from '../remix-icon/remix-icon';
import styles from './mantine-modal.module.css';

type ModalPosition =
  | 'center'
  | 'bottom-right'
  | 'bottom-left'
  | 'top-right'
  | 'top-left';

interface MantineModalProps extends ModalProps {
  centered?: boolean;
  position?: ModalPosition;
  className?: string;
}

const MantineModal = ({
  children,
  size = 520,
  title,
  mah = 640,
  withOverlay = true,
  withCloseButton = false,
  closeButtonProps,
  centered = true,
  position = 'center',
  className,
  ...props
}: MantineModalProps) => {
  const shouldCenter = position === 'center' ? centered : false;

  return (
    <Modal.Root
      size={size}
      zIndex={1000}
      {...props}
      centered={shouldCenter}
      classNames={{
        content: clsx(styles.content, className),
      }}
    >
      {withOverlay && <Modal.Overlay backgroundOpacity={0.5} />}
      <Modal.Content
        aria-label={typeof title === 'string' ? title : undefined}
        mah={mah}
        data-position={position}
      >
        {withCloseButton && (
          <Modal.CloseButton
            className={styles.cancel}
            icon={<RIcon icon={RiCloseLine} />}
            {...closeButtonProps}
          />
        )}
        {children}
      </Modal.Content>
    </Modal.Root>
  );
};

const Header = memo(({ className, ...props }: ModalHeaderProps) => (
  <Modal.Header className={clsx(styles.header, className)} {...props} />
));

const Body = memo(
  forwardRef<HTMLDivElement, ModalBodyProps>(({ className, ...props }, ref) => (
    <Modal.Body ref={ref} className={clsx(styles.body, className)} {...props} />
  ))
);

const Footer = memo(
  ({ className, ...props }: ComponentPropsWithoutRef<'div'>) => (
    <div className={clsx(styles.footer, className)} {...props} />
  )
);

Header.displayName = 'ModalHeader';
Body.displayName = 'ModalBody';
Footer.displayName = 'ModalFooter';

export default Object.assign(memo(MantineModal), {
  Header: memo(Header),
  Body: memo(Body),
  Footer: memo(Footer),
  CloseButton: Modal.CloseButton,
});
